import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertTriangle, ShieldCheck, Loader2, Info, ChevronRight, ChevronDown, BarChart3 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format, parseISO } from 'date-fns';
import { useAnomalyResults, AnomalyResult } from '@/hooks/useAnomalyResults';

interface AnomalySummarySectionProps {
  organisationId: string;
  orgCreatedAt?: string;
}

const METRIC_LABELS: Record<string, string> = {
  order_count: 'Orders',
  units_sold: 'Units Sold',
  gmv: 'GMV',
  avg_order_value: 'Avg Order Value',
  refund_count: 'Refunds',
  active_sku_count: 'Active SKUs',
  data_freshness: 'Data Freshness',
  connection: 'Connection',
};

const METRIC_ORDER: Record<string, number> = {
  order_count: 0,
  units_sold: 1,
  gmv: 2,
  avg_order_value: 3,
  active_sku_count: 4,
  refund_count: 5,
  data_freshness: 6,
  connection: 7,
};

function formatMetricValue(metric: string, value: number | null): string {
  if (value === null) return '--';

  if (metric === 'gmv' || metric === 'avg_order_value') {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  if (metric === 'data_freshness') {
    if (value >= 24) {
      const days = Math.round(value / 24);
      return `${days} days ago`;
    }
    return `${value}h ago`;
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatDeviation(deviationPct: number | null, anomalyDate?: string): string {
  if (deviationPct === null) return '--';
  const weekday = anomalyDate
    ? format(parseISO(anomalyDate), 'EEEE')
    : null;
  const suffix = weekday ? ` vs. your usual ${weekday}` : '';
  const sign = deviationPct >= 0 ? '+' : '';
  return `${sign}${deviationPct.toFixed(0)}%${suffix}`;
}

function SeverityBadge({ severity }: { severity: string }) {
  if (severity === 'critical') {
    return <Badge variant="destructive">Critical</Badge>;
  }
  if (severity === 'warning') {
    return <Badge variant="warning">Warning</Badge>;
  }
  return <Badge variant="secondary">Info</Badge>;
}

function CriticalBanner({ anomalies }: { anomalies: AnomalyResult[] }) {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const cutoff = sevenDaysAgo.toISOString().split('T')[0];

  const recentCritical = anomalies.filter(
    a => a.severity === 'critical' && a.anomaly_date >= cutoff
  );

  if (recentCritical.length === 0) return null;

  const mostRecent = recentCritical[0];

  return (
    <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-medium text-red-800 dark:text-red-300">
          {recentCritical.length} critical flag{recentCritical.length !== 1 ? 's' : ''} in the last 7 days
        </p>
        <p className="text-sm text-red-700 dark:text-red-400 mt-0.5">
          Most recent: {mostRecent.check_name} &mdash; {format(parseISO(mostRecent.anomaly_date), 'MMM d')}
        </p>
      </div>
    </div>
  );
}

interface DateGroup {
  date: string;
  anomalies: AnomalyResult[];
  highestSeverity: string;
}

function getHighestSeverity(anomalies: AnomalyResult[]): string {
  if (anomalies.some(a => a.severity === 'critical')) return 'critical';
  if (anomalies.some(a => a.severity === 'warning')) return 'warning';
  return 'info';
}

function AnomalyMetricRow({ anomaly }: { anomaly: AnomalyResult }) {
  const isFreshness = anomaly.check_type === 'freshness';
  const isConnection = anomaly.metric === 'connection';

  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pl-8 pr-4 font-medium text-sm">
        {isConnection
          ? anomaly.check_name || 'Connection Error'
          : (METRIC_LABELS[anomaly.metric] || anomaly.metric)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-sm text-gray-700">
        {isConnection
          ? anomaly.error_message || '--'
          : formatMetricValue(anomaly.metric, anomaly.actual_value)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-sm text-gray-500">
        {isFreshness || isConnection ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-gray-400">N/A</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs max-w-[200px]">
                {isFreshness
                  ? 'Freshness checks use a fixed threshold, not a weekday baseline'
                  : 'Connection checks do not use a baseline comparison'}
              </p>
            </TooltipContent>
          </Tooltip>
        ) : (
          formatMetricValue(anomaly.metric, anomaly.expected_value)
        )}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-sm text-gray-600">
        {isFreshness || isConnection ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-gray-400">N/A</span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs max-w-[200px]">
                {isFreshness
                  ? 'Compared against a fixed staleness threshold (48h critical, 24h warning)'
                  : 'Not applicable for connection errors'}
              </p>
            </TooltipContent>
          </Tooltip>
        ) : (
          formatDeviation(anomaly.deviation_pct, anomaly.anomaly_date)
        )}
      </td>
      <td className="py-2">
        <SeverityBadge severity={anomaly.severity} />
      </td>
    </tr>
  );
}

function AnomalyTable({ anomalies }: { anomalies: AnomalyResult[] }) {
  if (anomalies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-gray-500">
        <ShieldCheck className="h-8 w-8 text-green-500 mb-2" />
        <p className="text-sm">No anomalies detected in the last 30 days.</p>
      </div>
    );
  }

  // Group by date, sort date descending
  const dateGroups = useMemo(() => {
    const groups: Record<string, AnomalyResult[]> = {};
    for (const a of anomalies) {
      const key = a.anomaly_date;
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    }

    // Sort each group by metric order
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => {
        return (METRIC_ORDER[a.metric] ?? 99) - (METRIC_ORDER[b.metric] ?? 99);
      });
    }

    return Object.entries(groups)
      .map(([date, items]): DateGroup => ({
        date,
        anomalies: items,
        highestSeverity: getHighestSeverity(items),
      }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [anomalies]);

  const defaultExpanded = dateGroups.length <= 5;
  const [expandedDates, setExpandedDates] = useState<Set<string>>(() => {
    return defaultExpanded ? new Set(dateGroups.map(g => g.date)) : new Set<string>();
  });

  const toggleDate = (date: string) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-4 font-medium">Metric</th>
              <th className="pb-2 pr-4 font-medium text-right">Value</th>
              <th className="pb-2 pr-4 font-medium text-right">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 cursor-help">
                      Baseline
                      <Info className="h-3 w-3 text-gray-400" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-[200px]">Average of the same weekday over the prior 6 weeks</p>
                  </TooltipContent>
                </Tooltip>
              </th>
              <th className="pb-2 pr-4 font-medium text-right">vs. Typical</th>
              <th className="pb-2 font-medium">Severity</th>
            </tr>
          </thead>
          <tbody>
            {dateGroups.map((group) => {
              const isExpanded = expandedDates.has(group.date);
              return (
                <React.Fragment key={group.date}>
                  <tr
                    className="border-b cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    onClick={() => toggleDate(group.date)}
                  >
                    <td colSpan={5} className="py-2.5">
                      <div className="flex items-center gap-2">
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                          : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                        }
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {format(parseISO(group.date), 'MMM d')}
                        </span>
                        <span className="text-gray-500">
                          &mdash; {group.anomalies.length} flag{group.anomalies.length !== 1 ? 's' : ''}
                        </span>
                        <SeverityBadge severity={group.highestSeverity} />
                      </div>
                    </td>
                  </tr>
                  {isExpanded && group.anomalies.map((anomaly) => (
                    <AnomalyMetricRow key={anomaly.id} anomaly={anomaly} />
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}

const AnomalySummarySection: React.FC<AnomalySummarySectionProps> = ({ organisationId, orgCreatedAt }) => {
  const { anomalies, criticalCount, warningCount, loading, error } = useAnomalyResults(organisationId);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400 mr-2" />
          <span className="text-sm text-gray-500">Loading anomaly data...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null;
  }

  // Determine which connection types are present
  const connectionTypes = [...new Set(anomalies.map(a => a.connection_type).filter(Boolean))] as string[];
  const hasMultipleSources = connectionTypes.length > 1;

  // Group anomalies by source
  const anomaliesBySource: Record<string, AnomalyResult[]> = {};
  for (const connType of connectionTypes) {
    anomaliesBySource[connType] = anomalies.filter(a => a.connection_type === connType);
  }

  // If no anomalies, show appropriate empty state
  if (connectionTypes.length === 0 && anomalies.length === 0) {
    // Check if org is too new for reliable baselines (< 8 weeks)
    const isNewOrg = orgCreatedAt
      ? (Date.now() - new Date(orgCreatedAt).getTime()) < (8 * 7 * 24 * 60 * 60 * 1000)
      : false;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-5 w-5 text-gray-400" />
            Data Anomalies
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-gray-500">
            {isNewOrg ? (
              <>
                <BarChart3 className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm font-medium">Not enough data yet</p>
                <p className="text-xs text-gray-400 mt-1">Anomaly detection needs at least 4 weeks of history to establish a baseline.</p>
              </>
            ) : (
              <>
                <ShieldCheck className="h-8 w-8 text-green-500 mb-2" />
                <p className="text-sm">No anomalies detected in the last 30 days.</p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const sourceLabel = (type: string) => {
    if (type === 'shopify') return 'Shopify';
    if (type === 'amazon') return 'Amazon';
    return type;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Data Anomalies
          </CardTitle>
          <div className="flex gap-2">
            {criticalCount > 0 && (
              <Badge variant="destructive">{criticalCount} critical</Badge>
            )}
            {warningCount > 0 && (
              <Badge variant="warning">{warningCount} warning</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <CriticalBanner anomalies={anomalies} />

        {hasMultipleSources ? (
          <Tabs defaultValue={connectionTypes[0]}>
            <TabsList>
              {connectionTypes.map(type => (
                <TabsTrigger key={type} value={type}>
                  {sourceLabel(type)}
                  {(anomaliesBySource[type]?.filter(a => a.severity === 'critical').length ?? 0) > 0 && (
                    <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                      {anomaliesBySource[type]?.filter(a => a.severity === 'critical').length}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
            {connectionTypes.map(type => (
              <TabsContent key={type} value={type}>
                <AnomalyTable anomalies={anomaliesBySource[type] || []} />
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <AnomalyTable anomalies={anomalies} />
        )}
      </CardContent>
    </Card>
  );
};

export default AnomalySummarySection;
